import type { Expediente } from '../ui/Homologacion.js';
/**
 * Cliente del flujo de conversacion.
 *
 * No se usa EventSource porque solo admite GET y aqui hace falta enviar el
 * mensaje y el token en el cuerpo. Se lee el SSE a mano sobre fetch, que
 * ademas permite cancelar la respuesta a media generacion (barge-in).
 */

export interface SessionEvent {
  token: string;
  sid: string;
  expiresAt: number;
}

export interface ChatHandlers {
  onSession?: (event: SessionEvent) => void;
  /** Fragmento incremental: pintarlo de inmediato. */
  onDelta?: (text: string) => void;
  /** Frase cerrada: es la unidad que se manda a sintetizar en el Hito 2. */
  onSentence?: (index: number, text: string) => void;
  /** Citas de la fuente oficial: se muestran debajo de la respuesta, no se dicen. */
  onSources?: (items: string[]) => void;
  onDone?: (text: string) => void;
  onError?: (message: string) => void;
}

/** Documento adjunto listo para enviar (base64 sin cabecera). */
export interface OutgoingAttachment {
  name: string;
  mediaType: string;
  data: string;
}

export interface ChatOptions extends ChatHandlers {
  message: string;
  attachments?: OutgoingAttachment[];
  /** El asesor ya se presento: el modelo no debe volver a presentarse. */
  greeted?: boolean;
  token?: string | null;
  signal?: AbortSignal;
}

interface SseFrame {
  event: string;
  data: string;
}

/** Trocea el cuerpo en marcos SSE completos (separados por linea en blanco). */
async function* readFrames(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncIterable<SseFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      if (signal?.aborted) return;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let split = buffer.indexOf('\n\n');
      while (split !== -1) {
        const raw = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);

        let event = 'message';
        const dataLines: string[] = [];
        for (const line of raw.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        if (dataLines.length > 0) yield { event, data: dataLines.join('\n') };

        split = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function sendMessage(options: ChatOptions): Promise<void> {
  const { message, attachments, greeted, token, signal, onSession, onDelta, onSentence, onSources, onDone, onError } = options;

  let response: Response;
  try {
    response = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ message, token: token ?? undefined, attachments: attachments?.length ? attachments : undefined, greeted: greeted || undefined }),
      signal,
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError') return;
    onError?.('No se pudo conectar con el servidor. Revisa tu conexion.');
    return;
  }

  if (!response.ok || !response.body) {
    const detail = await response.json().catch(() => null);
    onError?.(
      (detail as { error?: string } | null)?.error ??
        'El servidor no pudo procesar la consulta.',
    );
    return;
  }

  try {
    for await (const frame of readFrames(response.body, signal)) {
      let payload: unknown;
      try {
        payload = JSON.parse(frame.data);
      } catch {
        continue;
      }

      switch (frame.event) {
        case 'session':
          onSession?.(payload as SessionEvent);
          break;
        case 'delta':
          onDelta?.((payload as { text: string }).text);
          break;
        case 'sentence': {
          const { index, text } = payload as { index: number; text: string };
          onSentence?.(index, text);
          break;
        }
        case 'sources':
          onSources?.((payload as { items: string[] }).items);
          break;
        case 'done':
          onDone?.((payload as { text: string }).text);
          break;
        case 'error':
          onError?.((payload as { message: string }).message);
          break;
      }
    }
  } catch (error) {
    if ((error as Error).name !== 'AbortError') {
      onError?.('Se interrumpio la respuesta. Intentalo de nuevo.');
    }
  }
}

export interface StoredTranscript {
  sid: string;
  token: string;
  expiresAt: number;
  messages: Array<{ role: 'user' | 'assistant'; content: string; createdAt: number; homologacion?: Expediente; sources?: string[] }>;
}

/** Recupera una conversacion desde el enlace firmado. */
export async function loadSession(
  token: string,
): Promise<{ ok: true; data: StoredTranscript } | { ok: false; error: string }> {
  const response = await fetch(`/api/session/${encodeURIComponent(token)}`);
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    return {
      ok: false,
      error: (body as { error?: string } | null)?.error ?? 'No se pudo abrir la conversacion.',
    };
  }
  return { ok: true, data: body as StoredTranscript };
}
